/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/Drawer.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/Drawer.tsx
 * filename: Drawer.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * Drawer Component
 * Side-mounted panel built on Radix Dialog primitives.
 * Used for contextual actions that need parent-page context (e.g. device logs,
 * ticket review, job wizards). Slides in from the right by default.
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { useReturnFocusOnClose } from './returnFocus';

const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;
const DrawerPortal = DialogPrimitive.Portal;

const drawerSizes = {
  sm: 'w-full sm:w-[420px]',
  md: 'w-full sm:w-[560px]',
  lg: 'w-full sm:w-[720px]',
  xl: 'w-full sm:w-[900px]',
  full: 'w-full sm:w-[min(1100px,85vw)]',
} as const;

type DrawerSize = keyof typeof drawerSizes;

interface DrawerContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  size?: DrawerSize;
  side?: 'right' | 'left';
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
}

const DrawerContent = ({
  className,
  children,
  size = 'lg',
  side = 'right',
  ref,
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...props
}: DrawerContentProps) => {
  const returnFocus = useReturnFocusOnClose(onOpenAutoFocus, onCloseAutoFocus);
  return (
    <DrawerPortal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none motion-reduce:transition-none" />
      <DialogPrimitive.Content
        ref={ref}
        className={clsx(
          'fixed top-0 z-50 h-full flex flex-col border-[rgb(var(--color-border-primary)/0.68)] bg-[rgb(var(--color-bg-primary))] shadow-[0_32px_90px_rgba(15,23,42,0.32)] duration-[var(--motion-duration-enter)] data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none motion-reduce:transition-none',
          side === 'right'
            ? 'right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
            : 'left-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
          drawerSizes[size],
          className
        )}
        {...props}
        {...returnFocus}
      >
        {children}
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
};

const DrawerHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={clsx(
      'flex items-start justify-between gap-4 border-b border-[rgb(var(--color-border-primary)/0.68)] px-6 py-4',
      className
    )}
    {...props}
  >
    <div className="flex-1 min-w-0">{children}</div>
    <DialogPrimitive.Close className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl p-0 text-[rgb(var(--color-text-tertiary))] transition-colors hover:bg-[rgb(var(--color-bg-secondary)/0.72)] hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dest-action-focus)]">
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </div>
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx('flex-1 overflow-y-auto px-6 py-4', className)} {...props} />
);
DrawerBody.displayName = 'DrawerBody';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={clsx(
      'flex items-center justify-end gap-2 border-t border-[rgb(var(--color-border-primary)/0.68)] px-6 py-4',
      className
    )}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Title>>;
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx('text-lg font-semibold text-[rgb(var(--color-text-primary))]', className)}
    {...props}
  />
);

const DrawerDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Description>>;
}) => (
  <DialogPrimitive.Description
    ref={ref}
    className={clsx('mt-1 text-sm text-[rgb(var(--color-text-secondary))]', className)}
    {...props}
  />
);

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerPortal,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
